import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useGetTicket, useUpdateTicket } from "../../api/tickets";
import { useGetUser } from "../../api/user";
import { EditUser } from "../Users/EditUser";
import { Button, Container, Fieldset, Text } from "@mantine/core";
import { useGetPeopleTagsBySubtype } from "../../api/tag";
import { useForm } from "react-hook-form";
import {
  Checkbox,
  MultiSelect,
  Select,
  TextInput,
} from "react-hook-form-mantine";
import { DatePickerInput } from "@mantine/dates";
import { Controller } from "react-hook-form";
import CountrySelect from "../CountrySelect";
import { capitalize } from "../../utils/textUtils";
import {
  useGetCustomFields,
  useUpdateCustomFields,
  useGetTicketColumns,
} from "../../api/tickets";
import { extractDirtyData } from "../../utils/dirtyData";
import {
  toCustomFieldInputValue,
  fromCustomFieldInputValue,
  optionData,
} from "../../utils/customFieldValues";

function Edit() {
  const { ticketId } = useParams();

  const { data: ticket, isError, error } = useGetTicket({ ticketId });

  const { data: user } = useGetUser(ticket?.userId);

  if (isError) {
    return <Text c="red">{error.message}</Text>;
  }

  return (
    <>
      {user && (
        <EditUser
          user={user}
          isAdmin
          currentEventId={ticket?.eventId}
          ticketId={ticket?.id}
        />
      )}
      {ticket && (
        <>
          <EditTicketTag ticket={ticket} />
          <EditTicketCustomFields ticket={ticket} />
        </>
      )}
    </>
  );
}

function EditTicketTag({ ticket }) {
  const { data } = useGetPeopleTagsBySubtype({
    eventId: ticket.eventId,
    subtype: "categoryOfUser",
  });

  const findUserTicketCategories = ticket?.tags?.filter(
    (tag) => tag.subtype === "categoryOfUser"
  );

  const { mutate, isPending } = useUpdateTicket({ ticketId: ticket.id });

  const defaultValues = {
    categoryOfUser: findUserTicketCategories?.map((tag) => String(tag.id)),
  };

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = useForm({
    defaultValues,
  });

  const handleTagUpdate = (data) => {
    mutate(
      {
        categoryOfUser: data.categoryOfUser,
      },
      {
        onSuccess: () => {
          reset({ categoryOfUser: data.categoryOfUser });
        },
        onError: () => {
          // Handle error
          reset({ ...defaultValues });
        },
      }
    );
  };

  return (
    <Container my="md">
      <form onSubmit={handleSubmit((data) => handleTagUpdate(data))}>
        <Fieldset legend="Ticket category">
          <MultiSelect
            label="Category"
            placeholder="Select category"
            data={data?.map((tag) => ({
              value: String(tag.id),
              label: capitalize(tag.title),
            }))}
            control={control}
            name="categoryOfUser"
          />
        </Fieldset>
        <Button
          type="submit"
          variant="light"
          color="blue"
          mt="md"
          disabled={!isDirty}
          loading={isPending}
        >
          Change category
        </Button>
      </form>
    </Container>
  );
}

const EditTicketCustomFields = ({ ticket }) => {
  const [errors, setErrors] = useState({});
  const { data: customFieldsData } = useGetCustomFields({
    eventId: ticket.eventId,
    ticketId: ticket.id,
  });

  const { data: columns } = useGetTicketColumns({
    eventId: ticket.eventId,
  });

  const customColumns = columns
    ? columns?.filter((col) => col.isCustom && col.isVisible)
    : [];

  const { mutate, isPending } = useUpdateCustomFields({
    eventId: ticket.eventId,
    ticketId: ticket.id,
  });

  const {
    control,
    handleSubmit,
    formState: { dirtyFields, isDirty },
    reset,
  } = useForm({ defaultValues: {} });

  useEffect(() => {
    // A refetch must not throw away what the organizer is in the middle of
    // typing, so the stored values only land in an untouched form.
    if (!columns || !customFieldsData || isDirty) return;

    reset(
      customColumns.reduce((acc, field) => {
        const stored = customFieldsData.find(
          (cf) => cf.name === field.name && cf.ticketId === ticket.id
        );
        acc[field.name] = toCustomFieldInputValue(
          stored?.value,
          field.type,
          field.options
        );
        return acc;
      }, {})
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, customFieldsData, ticket.id, reset]);

  const handleCustomFieldsUpdate = (data) => {
    const dirtyData = extractDirtyData(data, dirtyFields);

    const payload = Object.entries(dirtyData).reduce((acc, [name, value]) => {
      const field = customColumns.find((column) => column.name === name);
      acc[name] = fromCustomFieldInputValue(value, field?.type);
      return acc;
    }, {});

    mutate(
      {
        data: payload,
        ticketId: ticket.id,
        eventId: ticket.eventId,
      },
      {
        onSuccess: () => {
          reset(data);
        },
      }
    );

    setErrors({});
  };

  const renderField = (field) => {
    const options = optionData(field.options || []);

    if (field.type === "date") {
      return (
        <Controller
          key={field.name}
          name={field.name}
          control={control}
          render={({ field: dateField }) => (
            <DatePickerInput
              label={field.name}
              placeholder="Pick date"
              valueFormat="DD.MM.YYYY."
              clearable
              value={dateField.value ?? null}
              onChange={(date) => dateField.onChange(date ?? null)}
            />
          )}
        />
      );
    }

    if (field.type === "boolean") {
      return (
        <Checkbox
          key={field.name}
          control={control}
          name={field.name}
          label={field.name}
          mt="sm"
        />
      );
    }

    if (field.type === "country") {
      return (
        <CountrySelect
          key={field.name}
          control={control}
          name={field.name}
          label={field.name}
          placeholder="Select country"
        />
      );
    }

    if (field.type === "select") {
      return (
        <Select
          key={field.name}
          control={control}
          name={field.name}
          label={field.name}
          data={options}
          clearable
        />
      );
    }

    if (field.type === "multiselect") {
      return (
        <MultiSelect
          key={field.name}
          control={control}
          name={field.name}
          label={field.name}
          data={options}
          clearable
        />
      );
    }

    return (
      <TextInput
        key={field.name}
        label={field.name}
        control={control}
        name={field.name}
        error={errors[field.name]}
        onChange={(e) => {
          // Reset the error message when the user starts typing
          setErrors((prev) => {
            const { [field.name]: thisFieldError, ...rest } = prev;
            return rest;
          });
          // If user types in only space, show an error message
          if (e.target.value.trim().length === 0 && e.target.value.length > 0) {
            setErrors((prev) => ({
              ...prev,
              [field.name]: "Field value cannot contain only spaces",
            }));
          }
        }}
      />
    );
  };

  return (
    <Container my="md">
      <form onSubmit={handleSubmit((data) => handleCustomFieldsUpdate(data))}>
        <Fieldset legend="Custom fields" name={"customFields"}>
          {customColumns?.map((field) => renderField(field))}
        </Fieldset>

        <Button
          type="submit"
          variant="light"
          color="blue"
          mt="md"
          disabled={!isDirty || Object.keys(errors).length > 0}
          loading={isPending}
        >
          Update custom fields
        </Button>
      </form>
    </Container>
  );
};

export default Edit;
